import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllEmployees } from '../../../store/slices/employeeManagementSlice';
import EmployeeDetailModal from './components/EmployeeDetailModal';
import './ViewEmployees.scss';

const ViewEmployees = () => {
  const dispatch = useDispatch();
  const { employees, loading, error } = useSelector(state => state.employeeManagement);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'empID', direction: 'ascending' });
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    dispatch(fetchAllEmployees());
  }, [dispatch]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleRowClick = (employee) => {
    setSelectedEmployee(employee);
  };

  const handleCloseModal = () => {
    setSelectedEmployee(null);
  };

  const filteredEmployees = employees.filter(employee => {
    return (
      (employee.name && employee.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (employee.empID && employee.empID.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (employee.email && employee.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (employee.designation && employee.designation.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (employee.organogram && employee.organogram.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    if (!a[sortConfig.key] || !b[sortConfig.key]) return 0;
    
    const aValue = a[sortConfig.key].toString().toLowerCase();
    const bValue = b[sortConfig.key].toString().toLowerCase();
    
    if (aValue < bValue) {
      return sortConfig.direction === 'ascending' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'ascending' ? 1 : -1;
    }
    return 0;
  });

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'ascending' ? '↑' : '↓';
  };

  return (
    <div className="view-employees-container">
      <div className="page-header">
        <h1>View Employees</h1>
        <p>Manage and view all employee records</p>
      </div>
      
      <div className="employees-controls">
        <div className="search-box">
          <input 
            type="text" 
            placeholder="Search employees..." 
            value={searchTerm} 
            onChange={handleSearch}
          />
        </div>
        <button className="refresh-btn" onClick={() => dispatch(fetchAllEmployees())}>
          Refresh
        </button>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading employees...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>{error}</p>
          <button onClick={() => dispatch(fetchAllEmployees())}>Try Again</button>
        </div>
      ) : (
        <div className="employees-table-container">
          {sortedEmployees.length === 0 ? (
            <div className="no-employees">
              <p>{searchTerm ? 'No employees match your search criteria.' : 'No employees have been added yet.'}</p>
            </div>
          ) : (
            <table className="employees-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('empID')}>
                    Employee ID {getSortIcon('empID')}
                  </th>
                  <th onClick={() => handleSort('name')}>
                    Name {getSortIcon('name')}
                  </th>
                  <th onClick={() => handleSort('email')}>
                    Email {getSortIcon('email')}
                  </th>
                  <th onClick={() => handleSort('designation')}>
                    Designation {getSortIcon('designation')}
                  </th>
                  <th onClick={() => handleSort('organogram')}>
                    Department {getSortIcon('organogram')}
                  </th>
                  <th onClick={() => handleSort('joinDate')}>
                    Join Date {getSortIcon('joinDate')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedEmployees.map((employee) => (
                  <tr 
                    key={employee.id} 
                    onClick={() => handleRowClick(employee)}
                    className="employee-row"
                  >
                    <td>{employee.empID}</td>
                    <td>
                      <div className="employee-name">
                        {employee.img && (
                          <img 
                            src={employee.img} 
                            alt={employee.name} 
                            className="employee-avatar"
                          />
                        )}
                        <span>{employee.name}</span>
                      </div>
                    </td>
                    <td>{employee.email}</td>
                    <td>{employee.designation}</td>
                    <td>{employee.organogram}</td>
                    <td>{employee.joinDate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      
      {selectedEmployee && (
        <EmployeeDetailModal 
          employee={selectedEmployee}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default ViewEmployees; 